/**
 * SKYPULSE bootstrap + screen router (M3):
 * Title → tutorial/Level Select/Daily/Ships → Pre-run → Run → Results.
 *
 * Loop architecture (spec 4.1/4.2/5): the Conductor is the master clock.
 * Each render frame ticks the conductor, steps the 120 Hz sim (and the PB
 * ghost's sim) until they catch up with conductor.songTime, then renders
 * interpolated poses one tick in the past. Daily tracks are generated from
 * the date seed; the scored attempt has no retries and writes exactly once.
 */
import {
  SIM_DT,
  DEATH_OVERLAY_DELAY_MS,
  jumpParamsForBpm,
  RESPAWN_PREROLL_BEATS,
  PRACTICE_COUNT_IN_BEATS,
  PALETTES,
  PALETTE_W1,
  FOV_MIN_DEG,
  FOV_MAX_DEG,
  FOV_SMOOTH_S,
  EFFRATE_MIN,
  EFFRATE_MAX,
  SPEEDLINE_MIN_EFFRATE,
  LANDING_IMPACT_MIN_DROP,
  NEARMISS_MIN_EFFRATE,
  NEARMISS_COOLDOWN_BEATS,
  ROW_DEPTH,
  CELL_WIDTH,
  laneCenterX,
  type WorldPalette,
} from "./config/tuning";
import { SilentClock, type Conductor } from "./audio/conductor";
import { AudioSystem } from "./audio/audioSystem";
import { AudioConductor } from "./audio/audioConductor";
import { SONGS, speedForSong, type SongAsset } from "./data/songs";
import { SaveStore } from "./data/saveStore";
import { DailyManager, dailyResultOf, shareString, type DailyResult } from "./data/daily";
import { COSMETICS, cosmeticById, evalUnlocks } from "./data/cosmetics";
import { parseLevel } from "./track/parser";
import type { LevelFile, ParsedLevel } from "./track/types";
import { generateDaily, dailyKey } from "./track/dailyGenerator";
import { TUTORIAL, CAMPAIGN, WORLD_NAMES, levelById, nextLevelId } from "./levels";
import { Sim, type InputFrame } from "./gameplay/sim";
import { InputLog, InputLogPlayer } from "./gameplay/inputLog";
import { makeGhost, serializeGhost, deserializeGhost, type GhostRecord } from "./gameplay/ghost";
import { KeyboardInput } from "./gameplay/keyboard";
import { TouchInput } from "./gameplay/touch";
import { GamepadInput } from "./gameplay/gamepad";
import { createScene, applyGlowQuality } from "./render/scene";
import { TrackRenderer } from "./render/trackRenderer";
import { createShipMesh, createGhostMesh, createEngineFlame } from "./render/shipMesh";
import { CameraRig } from "./render/cameraRig";
import { BeatFx } from "./render/beatFx";
import { DeathFx } from "./render/deathFx";
import { ImpactFx } from "./render/impactFx";
import { SkyboxFx } from "./render/skybox";
import { ShipTrail } from "./render/shipTrail";
import { QUALITY, benchmarkTier, type QualityTier } from "./core/quality";
import { BEAT_PULSE_DECAY } from "./config/tuning";
import { Hud, gravityLabel, applyGravityClass } from "./ui/hud";
import { SettingsUI } from "./ui/settings";
import { CalibrationFlow } from "./ui/calibration";
import { renderLevelList, renderPreRun, renderPracticeList, renderResults } from "./ui/screens";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3, Matrix } from "@babylonjs/core/Maths/math.vector";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";

type AppState =
  | "title"
  | "settings"
  | "calibration"
  | "touchhint"
  | "levelselect"
  | "prerun"
  | "practice"
  | "daily"
  | "ships"
  | "running"
  | "paused"
  | "dead"
  | "results";

type RunMode = "campaign" | "daily-scored" | "daily-practice";

function mergeFrames(a: InputFrame, b: InputFrame, c: InputFrame): InputFrame {
  return {
    steer: Math.max(-1, Math.min(1, a.steer + b.steer + c.steer)),
    jumpHeld: a.jumpHeld || b.jumpHeld || c.jumpHeld,
    accel: a.accel || b.accel || c.accel,
    brake: a.brake || b.brake || c.brake,
  };
}

function byId<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

async function copyToClipboard(text: string, button: HTMLElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    const prev = button.textContent;
    button.textContent = "Copied!";
    setTimeout(() => (button.textContent = prev), 1200);
  } catch {
    button.textContent = text; // clipboard blocked — show the text itself
  }
}

function main(): void {
  // WebGL2 gate (Section 1): friendly screen instead of a broken canvas
  if (!document.createElement("canvas").getContext("webgl2")) {
    byId("screen-title").classList.add("hidden");
    byId("screen-unsupported").classList.remove("hidden");
    return;
  }
  if ("serviceWorker" in navigator && import.meta.env.PROD) {
    void navigator.serviceWorker.register("/sw.js");
  }

  const canvas = byId<HTMLCanvasElement>("game-canvas");
  const hud = new Hud();
  const save = SaveStore.load();
  const audio = new AudioSystem();
  const daily = new DailyManager(save);

  const { engine, scene, camera } = createScene(canvas);
  // debug/automation handle (harmless: scene inspection only)
  (window as unknown as Record<string, unknown>).__scene = scene;
  const ship = createShipMesh(scene);
  const flame = createEngineFlame(scene, ship);
  const ghostMesh = createGhostMesh(scene);
  const camRig = new CameraRig(camera);
  camRig.setReducedMotion(save.settings.reducedMotion);
  const deathFx = new DeathFx(scene);
  const impactFx = new ImpactFx(scene);
  const trail = new ShipTrail(scene, ship);
  hud.initSpeedLines();
  let skybox: SkyboxFx | null = null;

  // --- quality tiers (Section 9) ---------------------------------------------
  let resolvedTier: QualityTier = "med";
  let speedLinesOn = true;
  function applyQuality(tier: QualityTier): void {
    resolvedTier = tier;
    const q = QUALITY[tier];
    applyGlowQuality(scene, q);
    deathFx.setEnabled(q.particles && !save.settings.reducedMotion);
    impactFx.setEnabled(q.particles);
    trail.setEnabled(q.trail && !save.settings.reducedMotion);
    speedLinesOn = q.speedLines && !save.settings.reducedMotion;
    if (!speedLinesOn) hud.setSpeedLines(0);
    if (track) {
      track.rowsAhead = q.rowsAhead;
      track.setDecorate(q.decorate);
      track.reset(sim ? sim.z : 0);
    }
    settingsUI.detectedTier = save.settings.quality === "auto" ? tier : null;
  }
  if (save.settings.quality === "auto") {
    // 2 s hidden benchmark behind the title screen (Section 9)
    void benchmarkTier().then((tier) => applyQuality(tier));
  } else {
    applyQuality(save.settings.quality);
  }

  function applyShipCosmetic(): void {
    const def = cosmeticById(save.cosmetics.equippedShip) ?? COSMETICS[0]!;
    (ship.material as StandardMaterial).emissiveColor = Color3.FromHexString(def.colorHex);
    const trailDef = cosmeticById(save.cosmetics.equippedTrail);
    if (trailDef) {
      trail.setStyle({
        colorHex: trailDef.colorHex,
        width: trailDef.trailWidth,
        alpha: trailDef.trailAlpha,
      });
    }
  }
  applyShipCosmetic();

  const kbd = new KeyboardInput();
  kbd.attach(window);
  const touch = new TouchInput();
  touch.attach(window);
  const pad = new GamepadInput();

  // --- per-level context ------------------------------------------------------
  interface LevelCtx {
    file: LevelFile;
    level: ParsedLevel;
    song: SongAsset;
    speed: number;
    palette: WorldPalette;
    spb: number;
    seedHash?: string; // daily only
  }
  let ctx: LevelCtx | null = null;
  let track: TrackRenderer | null = null;
  let beatFx: BeatFx | null = null;
  let conductor: Conductor | null = null;

  // --- per-attempt state ------------------------------------------------------
  let sim: Sim | null = null;
  let log = new InputLog();
  let runMode: RunMode = "campaign";
  let pureRun = false;
  let practiceMode = false;
  let attemptDeaths = 0;
  let usedCheckpoint = false;
  let startedFromStart = true;
  let state: AppState = "title";
  let deathOverlayTimer: ReturnType<typeof setTimeout> | null = null;
  let prerunLevelId: string | null = null;
  let practiceReturnState: AppState = "prerun";
  let lastResultsLevelId: string | null = null;
  let lastShare: string | null = null;

  // --- ghost state ------------------------------------------------------------
  let ghostRecord: GhostRecord | null = null;
  let ghostSim: Sim | null = null;
  let ghostPlayer: InputLogPlayer | null = null;

  // --- juice state (R2-M4) ----------------------------------------------------
  let maxAirY = -Infinity; // apex tracker for the landing-impact drop height
  let nearMissRow = -1;
  let nearMissCooldownBeat = -Infinity;

  function setState(next: AppState): void {
    state = next;
    switch (next) {
      case "title":
        byId("btn-daily").textContent =
          daily.streak > 1 ? `Daily 🔥${daily.streak}` : "Daily";
        hud.showScreen("title");
        hud.setHudVisible(false);
        break;
      case "settings":
        settingsUI.refresh();
        hud.showScreen("settings");
        break;
      case "calibration":
        hud.showScreen("calibration");
        break;
      case "touchhint":
        hud.showScreen("touchhint");
        break;
      case "levelselect":
        renderLevelList(CAMPAIGN, save, (id) => void openPreRun(id));
        hud.showScreen("levelselect");
        hud.setHudVisible(false);
        break;
      case "prerun":
        hud.showScreen("prerun");
        break;
      case "practice":
        hud.showScreen("practice");
        break;
      case "daily":
        renderDailyScreen();
        hud.showScreen("daily");
        hud.setHudVisible(false);
        break;
      case "ships":
        renderShipsScreen();
        hud.showScreen("ships");
        break;
      case "running":
        hud.showScreen(null);
        hud.setHudVisible(true);
        break;
      case "paused":
        hud.showScreen("paused");
        break;
      case "dead":
      case "results":
        break;
    }
  }

  // --- level loading ----------------------------------------------------------
  async function loadLevelFile(file: LevelFile, seedHash?: string): Promise<void> {
    const song = SONGS[file.songId];
    if (!song) throw new Error(`unknown song "${file.songId}"`);
    const level = parseLevel(file, { requireBits: file.id !== "tutorial", bpm: song.bpm });
    const speed = speedForSong(song);
    const palette = PALETTES[level.palette] ?? PALETTE_W1;
    const spb = 60 / song.bpm;

    track?.dispose();
    beatFx?.dispose();
    skybox?.dispose();
    conductor?.stop();

    const q = QUALITY[resolvedTier];
    track = new TrackRenderer(scene, level, palette, q.rowsAhead, q.decorate);
    const mats = track.pulsedMaterials;
    beatFx = new BeatFx(scene, mats.neon, mats.hazard, mats.boost, palette, save.settings.reducedMotion);
    skybox = new SkyboxFx(scene, palette, file.world, save.settings.reducedMotion);
    // world-palette accents flow into every screen (R2-M4 §6.5)
    document.documentElement.style.setProperty("--neon", palette.neon);
    document.documentElement.style.setProperty("--accent", palette.accent);

    audio.ensureUnlocked();
    audio.setMusicVolume(save.settings.musicVol);
    audio.setSfxVolume(save.settings.sfxVol);
    audio.setSongKey(song.key);
    const loaded = await audio.loadSong(song);
    if (loaded) {
      loaded.setUserLatencyOffsetMs(save.settings.latencyOffsetMs);
      conductor = loaded;
    } else {
      conductor = new SilentClock(song); // fully playable, identical difficulty (5.5)
    }

    ctx = {
      file,
      level,
      song,
      speed,
      palette,
      spb,
      seedHash,
    };
  }

  // --- ghost control ----------------------------------------------------------
  function armGhost(record: GhostRecord | null): void {
    ghostRecord = record;
  }

  function spawnGhost(startTick: number): void {
    ghostSim = null;
    ghostPlayer = null;
    ghostMesh.setEnabled(false);
    if (!ghostRecord || !ctx) return;
    ghostSim = new Sim(ctx.level, {
      speed: ctx.speed,
      jump: jumpParamsForBpm(ctx.song.bpm, ctx.level.gravity),
      secondsPerBeat: ctx.spb,
      startTick: ghostRecord.startTick,
    });
    ghostPlayer = new InputLogPlayer({
      levelId: ghostRecord.levelId,
      tuningVersion: ghostRecord.tuningVersion,
      entries: ghostRecord.entries,
    });
    // fast-forward to a mid-track start (respawn/practice) — musical z keeps
    // the ghost in sync wherever the run begins
    while (ghostSim.tick < startTick && ghostSim.status === "running" && ghostSim.tick < ghostRecord.endTick) {
      ghostSim.step(ghostPlayer.frameAt(ghostSim.tick + 1));
    }
    ghostMesh.setEnabled(true);
  }

  // --- run control ------------------------------------------------------------
  function beginRun(startRow: number, preRollBeats: number, carryBits = 0): void {
    if (!ctx || !track || !conductor) return;
    if (deathOverlayTimer !== null) {
      clearTimeout(deathOverlayTimer);
      deathOverlayTimer = null;
    }
    const startTick = startRow > 0 ? Sim.tickForRow(startRow, ctx.speed) : 0;
    sim = new Sim(ctx.level, {
      speed: ctx.speed,
      jump: jumpParamsForBpm(ctx.song.bpm, ctx.level.gravity),
      secondsPerBeat: ctx.spb,
      startTick,
    });
    sim.bitsMask = carryBits;
    sim.on("bitCollected", (i) => {
      track?.setBitCollected(i);
      audio.sfx("bit");
      // sparkle at the gem + a glint that flies to the HUD counter (VFX-4)
      const bit = ctx?.level.bits[i];
      if (bit && ctx) {
        const wx = laneCenterX(bit.lane);
        const wz = bit.row * ROW_DEPTH + ROW_DEPTH / 2;
        const p = Vector3.Project(
          new Vector3(wx, 0.85, wz),
          Matrix.IdentityReadOnly,
          scene.getTransformMatrix(),
          camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
        );
        hud.flyGlint(
          (p.x / engine.getRenderWidth()) * canvas.clientWidth,
          (p.y / engine.getRenderHeight()) * canvas.clientHeight,
        );
      }
    });
    sim.on("jumped", (onBeat) => {
      audio.sfx("jump");
      if (onBeat) {
        audio.sfx("onbeat");
        hud.flashOnBeat();
        // VFX-8: ring pulse around the ship + a one-beat trail brightness kick
        if (sim && ctx) impactFx.beatRing(sim.x, sim.y, sim.z, ctx.palette.neon);
        trail.kick(1);
      }
    });
    sim.on("landed", () => {
      audio.sfx("land");
      // VFX-2: landing impact for real drops; heavier on the heavy preset
      if (sim && ctx && maxAirY - sim.y >= LANDING_IMPACT_MIN_DROP) {
        const heavy = ctx.level.gravity === "heavy";
        impactFx.landing(sim.x, sim.y, sim.z, ctx.palette.neon, heavy);
        if (heavy) camRig.nudge();
      }
      maxAirY = -Infinity;
    });
    sim.on("checkpoint", () => {
      audio.sfx("checkpoint");
      // VFX-3: double activation ring + the row's glow stays lit behind you
      if (sim && ctx) {
        impactFx.checkpoint(sim.x, sim.lastCheckpointRow * ROW_DEPTH + ROW_DEPTH / 2, ctx.palette.accent);
      }
    });
    sim.on("headBump", () => {
      // thunk lands on the nearest upcoming 16th (R2 §2.1); the spark burst
      // and 40 ms nudge fire immediately
      if (conductor && ctx && sim) {
        const sixteenth = (60 / ctx.song.bpm) / 4;
        const phase16 = ((conductor.beat * 4) % 1 + 1) % 1;
        const delayReal = ((1 - phase16) * sixteenth) / Math.max(sim.effRate, 0.5);
        audio.sfx("thunk", Math.min(delayReal, sixteenth));
        deathFx.spark(sim.x, sim.y + 0.45, sim.z);
        camRig.nudge();
      }
    });
    sim.on("boost", (dir) => {
      if (dir > 0) {
        audio.sfx("boost");
        camRig.shake();
      }
    });
    log = new InputLog();
    audio.openFilter();
    conductor.restartAt(startTick * SIM_DT - preRollBeats * ctx.spb);
    impactFx.reset();
    camRig.stopOrbit();
    maxAirY = -Infinity;
    nearMissRow = -1;
    const z0 = sim.z;
    track.reset(z0);
    for (let i = 0; i < ctx.level.bits.length; i++) {
      if (carryBits & (1 << i)) track.setBitCollected(i);
    }
    spawnGhost(startTick);
    ship.setEnabled(true);
    ship.position.set(0, 0.3, z0);
    // pure-run aura (6.2): the hull burns brighter when checkpoints are off
    applyShipCosmetic();
    if (pureRun && !practiceMode) {
      (ship.material as StandardMaterial).emissiveColor.scaleInPlace(1.4);
    }
    trail.restart();
    camRig.snap(0, 0, z0);
    hud.setGravity(ctx.level.gravity);
    setState("running");
  }

  function startAttempt(): void {
    attemptDeaths = 0;
    usedCheckpoint = false;
    startedFromStart = true;
    beginRun(0, 0);
  }

  function respawnAtCheckpoint(): void {
    if (!sim || !ctx) return;
    usedCheckpoint = true;
    beginRun(sim.lastCheckpointRow, RESPAWN_PREROLL_BEATS, sim.bitsMask);
  }

  function retryFromDeath(): void {
    if (!sim || !ctx) return;
    if (runMode === "daily-scored") return; // one attempt — the lock (6.3)
    if (!practiceMode && !pureRun && sim.lastCheckpointRow > 0) {
      respawnAtCheckpoint();
    } else if (practiceMode && sim.startTick > 0) {
      beginRun(Math.floor((sim.speed * sim.startTick * SIM_DT) / 2), PRACTICE_COUNT_IN_BEATS);
    } else {
      startAttempt();
    }
  }

  async function openPreRun(id: string): Promise<void> {
    const file = levelById(id);
    if (!file) return;
    prerunLevelId = id;
    renderPreRun(file, save);
    byId<HTMLInputElement>("prerun-pure").checked = false;
    const rawGhost = save.getGhost(id);
    const ghost = rawGhost ? deserializeGhost(rawGhost) : null;
    byId("prerun-ghost-row").classList.toggle("hidden", ghost === null);
    byId<HTMLInputElement>("prerun-ghost").checked = ghost !== null;
    setState("prerun");
    await loadLevelFile(file); // preload while the player reads the screen
  }

  async function startLevel(id: string, pure: boolean, raceGhost: boolean): Promise<void> {
    const file = levelById(id);
    if (!file) return;
    if (!ctx || ctx.file.id !== id) await loadLevelFile(file);
    runMode = "campaign";
    pureRun = pure;
    practiceMode = false;
    const rawGhost = raceGhost ? save.getGhost(id) : undefined;
    armGhost(rawGhost ? deserializeGhost(rawGhost) : null);
    startAttempt();
  }

  async function startTutorial(): Promise<void> {
    await loadLevelFile(TUTORIAL);
    runMode = "campaign";
    pureRun = false;
    practiceMode = false;
    armGhost(null);
    startAttempt();
  }

  function startPractice(startRow: number): void {
    practiceMode = true;
    attemptDeaths = 0;
    usedCheckpoint = false;
    startedFromStart = startRow === 0;
    beginRun(startRow, startRow > 0 ? PRACTICE_COUNT_IN_BEATS : 0);
  }

  // --- daily ------------------------------------------------------------------
  function todayKey(): string {
    return dailyKey(new Date());
  }

  async function startDaily(scored: boolean): Promise<void> {
    const key = todayKey();
    const gen = generateDaily(key);
    await loadLevelFile(gen.file, gen.seedHash);
    runMode = scored ? "daily-scored" : "daily-practice";
    pureRun = scored; // scored attempt: no checkpoint respawns
    practiceMode = !scored;
    if (!scored && save.dailyGhost?.date === key) {
      armGhost(deserializeGhost(save.dailyGhost.ghost));
    } else {
      armGhost(null);
    }
    startAttempt();
  }

  function recordDailyOutcome(finished: boolean): void {
    if (!sim || !ctx || runMode !== "daily-scored") return;
    const key = todayKey();
    const result: DailyResult = dailyResultOf(
      key,
      finished ? 100 : sim.progressPct,
      finished ? sim.finishTick * SIM_DT * 1000 : undefined,
      ctx.seedHash ?? "",
    );
    if (daily.recordScored(result)) {
      lastShare = shareString(result, daily.streak);
      // keep the scored attempt's ghost for today's practice (6.6)
      save.setDailyGhost(
        key,
        serializeGhost(makeGhost(log.toRecord(ctx.file.id), 0, sim.tick, result.timeMs)),
      );
    }
    evalUnlocks(save); // streak cosmetics can unlock on scoring
  }

  function renderDailyScreen(): void {
    const key = todayKey();
    const scored = daily.hasScored(key);
    byId("daily-title").textContent = `Daily ${key}`;
    // today's rules up front (SkyRoads update): world + seeded gravity preset
    const gen = generateDaily(key);
    const gravEl = byId("daily-gravity");
    gravEl.textContent = `WORLD ${gen.file.world} — ${WORLD_NAMES[gen.file.world] ?? ""} · ${gravityLabel(gen.gravity, true)}`;
    gravEl.className = "gravity-tag";
    applyGravityClass(gravEl, gen.gravity);
    const today = daily.todayResult(key);
    byId("daily-status").textContent = scored && today
      ? `Today: ${Math.floor(today.progressPct)}%${today.timeMs !== undefined ? ` — ${formatMs(today.timeMs)}` : ""} · streak ${daily.streak}`
      : `One scored attempt — no retries. Streak ${daily.streak}`;
    const best = daily.bestEver();
    byId("daily-best").textContent = best
      ? `Best ever: ${Math.floor(best.progressPct)}%${best.timeMs !== undefined ? ` — ${formatMs(best.timeMs)}` : ""}`
      : "";
    byId("btn-daily-play").textContent = scored ? "Practice today's run" : "Play scored attempt";
    byId("btn-daily-share").classList.toggle("hidden", !scored);
  }

  function formatMs(ms: number): string {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}:${s.toString().padStart(2, "0")}.${Math.round(ms % 1000)
      .toString()
      .padStart(3, "0")}`;
  }

  // --- ships ------------------------------------------------------------------
  function renderShipsScreen(): void {
    const list = byId("ship-list");
    list.textContent = "";
    const section = (label: string): void => {
      const h = document.createElement("div");
      h.className = "world-header";
      h.textContent = label;
      list.appendChild(h);
    };
    const row = (c: (typeof COSMETICS)[number], equipped: boolean, onPick: () => void): void => {
      const unlocked =
        save.cosmetics.unlocked.includes(c.id) || c.id === "default" || c.id === "trail-default";
      const btn = document.createElement("button");
      btn.className = `ship-item${unlocked ? "" : " locked"}`;
      btn.innerHTML =
        `<span class="ship-swatch" style="background:${c.colorHex}"></span>` +
        `<span>${c.name}${equipped ? " ✓" : ""}</span>` +
        `<span class="earn">${unlocked ? "" : c.earn}</span>`;
      if (unlocked) btn.addEventListener("click", onPick);
      list.appendChild(btn);
    };
    section("HULLS");
    for (const c of COSMETICS.filter((c) => c.kind === "ship")) {
      row(c, save.cosmetics.equippedShip === c.id, () => {
        save.equipShip(c.id);
        applyShipCosmetic();
        renderShipsScreen();
      });
    }
    section("TRAILS");
    for (const c of COSMETICS.filter((c) => c.kind === "trail")) {
      row(c, save.cosmetics.equippedTrail === c.id, () => {
        save.cosmetics.equippedTrail = c.id;
        save.persist();
        applyShipCosmetic();
        renderShipsScreen();
      });
    }
  }

  // --- death & finish ---------------------------------------------------------
  function onDeath(): void {
    if (!sim || !ctx) return;
    attemptDeaths++;
    // death feedback ≤ 300 ms (3.3): shatter + low-pass sweep + shake
    ship.setEnabled(false);
    trail.hide();
    const shipDef = cosmeticById(save.cosmetics.equippedShip) ?? COSMETICS[0]!;
    deathFx.burst(sim.x, sim.y, sim.z, shipDef.colorHex);
    audio.sfx("death");
    audio.deathSweep();
    camRig.shake();
    hud.setSpeedLines(0);
    // VFX-7: one 250 ms desaturation ripple (single event, well under 3 Hz)
    if (!save.settings.reducedMotion) hud.flashRipple();
    if (runMode === "daily-scored") {
      recordDailyOutcome(false);
      showRunResults(false);
      return;
    }
    state = "dead";
    const respawnAvailable = !pureRun && !practiceMode && sim.lastCheckpointRow > 0;
    hud.setDeathInfo(sim.progressPct, respawnAvailable);
    deathOverlayTimer = setTimeout(() => {
      hud.showScreen("dead");
      deathOverlayTimer = null;
    }, DEATH_OVERLAY_DELAY_MS);
  }

  function onFinish(): void {
    if (!sim || !ctx) return;
    // VFX-5: palette confetti + the camera's slow victory orbit
    deathFx.confetti(sim.x, sim.y, sim.z, ctx.palette.accent);
    camRig.startOrbit();
    hud.setSpeedLines(0);
    if (runMode === "daily-scored") recordDailyOutcome(true);
    showRunResults(true);
  }

  function showRunResults(finished: boolean): void {
    if (!sim || !ctx) return;
    state = "results";
    const timeMs = sim.finishTick > 0 ? sim.finishTick * SIM_DT * 1000 : 0;
    const isTutorial = ctx.file.id === "tutorial";
    const isDaily = runMode !== "campaign";
    lastResultsLevelId = ctx.file.id;

    let entry;
    let isNewBest = false;
    let deathlessEarned = false;
    let unlockLine = "";
    if (finished && !practiceMode && !isTutorial && !isDaily) {
      deathlessEarned = pureRun && attemptDeaths === 0 && startedFromStart && !usedCheckpoint;
      const res = save.recordFinish(ctx.file.id, {
        timeMs,
        bitsMask: sim.bitsMask,
        deathless: deathlessEarned,
      });
      entry = res.entry;
      isNewBest = res.isNewBest;
      // PB ghost: only clean full-track clears are replayable as one log
      if (isNewBest && sim.startTick === 0) {
        save.setGhost(
          ctx.file.id,
          serializeGhost(makeGhost(log.toRecord(ctx.file.id), 0, sim.finishTick, timeMs)),
        );
      }
      const fresh = evalUnlocks(save);
      if (fresh.length > 0) {
        unlockLine = `Unlocked: ${fresh.map((c) => c.name).join(", ")}`;
      }
    }
    if (finished && isTutorial) save.markTutorialDone();

    renderResults({
      finished,
      practice: practiceMode,
      timeMs,
      progressPct: sim.progressPct,
      bitsThisRun: sim.bitsCollected,
      levelName: ctx.file.name,
      medals: ctx.file.medalTimesMs,
      entry,
      isNewBest,
      deathlessEarned,
      hasNext: !isTutorial && !isDaily && nextLevelId(ctx.file.id) !== undefined,
      tutorial: isTutorial,
    });
    if (sim.onBeatJumps > 0) {
      const grid = byId("results-grid");
      grid.innerHTML += `<span>on beat ×${sim.onBeatJumps}</span>`;
    }
    byId("results-unlocks").textContent = unlockLine;
    if (isDaily && runMode === "daily-scored" && lastShare) {
      byId("results-medal").textContent = "Daily scored. Come back tomorrow — or practice.";
    }
    byId("btn-results-share").classList.toggle(
      "hidden",
      !(runMode === "daily-scored" && lastShare !== null),
    );
    hud.showScreen("results");
  }

  // --- settings & calibration -------------------------------------------------
  const settingsUI = new SettingsUI(save, audio, {
    onBack: () => setState("title"),
    onCalibrate: () => {
      setState("calibration");
      calibration.start();
    },
    onReducedMotion: (on) => {
      beatFx?.setReducedMotion(on);
      camRig.setReducedMotion(on);
      skybox?.setReducedMotion(on);
      applyQuality(resolvedTier); // re-evaluate particles/trail/speed-line gating
    },
    onQuality: (setting) => {
      if (setting === "auto") void benchmarkTier().then((t) => applyQuality(t));
      else applyQuality(setting);
    },
    onResetSave: () => {
      localStorage.removeItem("skypulse-save");
      location.reload();
    },
  });

  const calibration = new CalibrationFlow(
    audio,
    { circle: byId("cal-circle"), status: byId("cal-status") },
    (offsetMs) => {
      if (offsetMs !== null) {
        save.updateSettings({ latencyOffsetMs: offsetMs });
        if (conductor instanceof AudioConductor) conductor.setUserLatencyOffsetMs(offsetMs);
      }
      settingsUI.refresh();
      setState("settings");
    },
  );

  // --- button wiring ----------------------------------------------------------
  byId("btn-settings").addEventListener("click", () => {
    audio.ensureUnlocked();
    setState("settings");
  });
  byId("btn-daily").addEventListener("click", () => {
    audio.ensureUnlocked();
    setState("daily");
  });
  byId("btn-ships").addEventListener("click", () => setState("ships"));
  byId("btn-ships-back").addEventListener("click", () => setState("title"));
  byId("btn-daily-back").addEventListener("click", () => setState("title"));
  byId("btn-daily-play").addEventListener("click", () => {
    void startDaily(!daily.hasScored(todayKey()));
  });
  byId("btn-daily-share").addEventListener("click", (e) => {
    const key = todayKey();
    const today = daily.todayResult(key);
    if (today) void copyToClipboard(shareString(today, daily.streak), e.currentTarget as HTMLElement);
  });
  byId("btn-results-share").addEventListener("click", (e) => {
    if (lastShare) void copyToClipboard(lastShare, e.currentTarget as HTMLElement);
  });
  byId("btn-cal-cancel").addEventListener("click", () => calibration.cancel());
  byId("btn-select-back").addEventListener("click", () => setState("title"));
  byId("btn-prerun-back").addEventListener("click", () => setState("levelselect"));
  byId("btn-prerun-start").addEventListener("click", () => {
    if (prerunLevelId) {
      void startLevel(
        prerunLevelId,
        byId<HTMLInputElement>("prerun-pure").checked,
        byId<HTMLInputElement>("prerun-ghost").checked,
      );
    }
  });
  byId("btn-prerun-practice").addEventListener("click", () => {
    if (ctx) {
      practiceReturnState = "prerun";
      runMode = "campaign";
      armGhost(null);
      renderPracticeList(ctx.level, (row) => startPractice(row));
      setState("practice");
    }
  });
  byId("btn-practice-back").addEventListener("click", () => setState(practiceReturnState));
  byId("btn-dead-restart").addEventListener("click", () => {
    if (practiceMode) startPractice(0);
    else startAttempt();
  });
  byId("btn-pause-resume").addEventListener("click", () => togglePause());
  byId("btn-pause-retry").addEventListener("click", () => {
    audio.resume();
    if (runMode === "daily-scored") return; // no retries on the scored attempt
    if (practiceMode) startPractice(0);
    else startAttempt();
  });
  byId("btn-pause-quit").addEventListener("click", () => {
    audio.resume();
    if (runMode === "daily-scored") {
      recordDailyOutcome(false); // quitting mid-scored-run records the progress
      conductor?.stop();
      setState("daily");
      return;
    }
    conductor?.stop();
    setState(runMode === "campaign" ? "levelselect" : "daily");
  });
  byId("btn-pause-practice").addEventListener("click", () => {
    if (ctx && runMode !== "daily-scored") {
      audio.resume();
      practiceReturnState = "paused";
      renderPracticeList(ctx.level, (row) => startPractice(row));
      setState("practice");
    }
  });
  byId("btn-results-retry").addEventListener("click", () => {
    if (runMode === "daily-scored") {
      void startDaily(false); // after scoring, retries are practice
    } else if (practiceMode && runMode === "campaign") {
      startPractice(0);
    } else if (runMode === "daily-practice") {
      void startDaily(false);
    } else {
      startAttempt();
    }
  });
  byId("btn-results-next").addEventListener("click", () => {
    const next = lastResultsLevelId ? nextLevelId(lastResultsLevelId) : undefined;
    if (next) void openPreRun(next);
    else setState("levelselect");
  });
  byId("btn-results-practice").addEventListener("click", () => {
    if (ctx) {
      practiceReturnState = "results";
      renderPracticeList(ctx.level, (row) => startPractice(row));
      setState("practice");
    }
  });
  byId("btn-results-select").addEventListener("click", () => {
    setState(runMode === "campaign" ? "levelselect" : "daily");
  });

  function togglePause(): void {
    if (state === "running") {
      audio.suspend();
      setState("paused");
    } else if (state === "paused") {
      audio.resume();
      setState("running");
    }
  }

  async function onTitleGo(): Promise<void> {
    audio.ensureUnlocked();
    // dev hook: ?dev-level=parts jumps straight into the parts playground
    if (new URLSearchParams(location.search).get("dev-level") === "parts") {
      const { PARTS_PLAYGROUND } = await import("./levels/partsPlayground");
      await loadLevelFile(PARTS_PLAYGROUND);
      runMode = "campaign";
      pureRun = false;
      practiceMode = true; // never writes records
      armGhost(null);
      startAttempt();
      return;
    }
    if (needsTouchHint()) {
      setState("touchhint");
      return;
    }
    if (!save.tutorialDone) {
      hud.setTitlePrompt("SYNCING…");
      await startTutorial();
      hud.setTitlePrompt("TAP OR PRESS ANY KEY TO START");
    } else {
      setState("levelselect");
    }
  }

  function needsTouchHint(): boolean {
    return (
      !save.settings.touchHintShown && (navigator.maxTouchPoints > 0 || "ontouchstart" in window)
    );
  }

  window.addEventListener("pointerdown", (e) => {
    // target can be a non-Element (window/document) for synthetic events
    const target = e.target as HTMLElement | null;
    if (target?.closest?.("button, input, .settings-panel, .select-panel, .results-panel"))
      return;
    switch (state) {
      case "title":
        void onTitleGo();
        break;
      case "touchhint":
        save.updateSettings({ touchHintShown: true });
        void onTitleGo();
        break;
      case "dead":
        retryFromDeath();
        break;
      case "calibration":
        calibration.tap();
        break;
      default:
        break;
    }
  });

  window.addEventListener("keydown", (e) => {
    if (state === "calibration" && e.code === "Space") calibration.tap();
    if (e.code === "Escape") {
      if (state === "levelselect" || state === "daily" || state === "ships") setState("title");
      else if (state === "prerun") setState("levelselect");
      else if (state === "practice") setState(practiceReturnState);
      else if (state === "results") setState(runMode === "campaign" ? "levelselect" : "daily");
    }
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden && state === "running") togglePause();
  });

  // --- the frame loop ---------------------------------------------------------
  engine.runRenderLoop(() => {
    const dt = Math.min(engine.getDeltaTime() / 1000, 0.1);
    pad.poll();

    if (kbd.consumeRetry()) {
      if (state === "dead") retryFromDeath();
      else if ((state === "running" || state === "paused") && runMode !== "daily-scored") {
        audio.resume();
        if (practiceMode && runMode === "campaign") startPractice(0);
        else if (runMode === "daily-practice") void startDaily(false);
        else startAttempt();
      } else if (state === "results" && runMode === "campaign") {
        if (practiceMode) startPractice(0);
        else startAttempt();
      }
    }
    if (kbd.consumePause() || pad.consumePause()) {
      if (state === "running" || state === "paused") togglePause();
    }
    const anyKey = kbd.consumeAnyKey();
    const padPress = pad.consumeJumpPress();
    if (state === "title" && (anyKey || padPress)) void onTitleGo();
    else if (state === "touchhint" && (anyKey || padPress)) {
      save.updateSettings({ touchHintShown: true });
      void onTitleGo();
    } else if (state === "dead" && padPress) retryFromDeath();

    // advance the dual clock: sim ticks schedule on REAL time (R2 §1.2);
    // effRate changes are pushed to the conductor (playbackRate follows)
    if (state === "running" && sim && conductor && ctx) {
      conductor.tick(dt);
      const targetTick = Math.floor(Math.max(0, conductor.realTime) / SIM_DT);
      let lastRate = sim.effRate;
      while (sim.tick < targetTick && sim.status === "running") {
        const frame = mergeFrames(kbd.frame(), touch.frame(sim.throttle), pad.frame());
        log.record(sim.tick + 1, frame);
        sim.step(frame);
        if (!sim.grounded && sim.y > maxAirY) maxAirY = sim.y; // apex (VFX-2)
        if (sim.effRate !== lastRate) {
          conductor.rateChangedAt(sim.tick, sim.effRate);
          lastRate = sim.effRate;
        }
      }
      conductor.syncFromSim(sim.tick, sim.songTime, sim.effRate);
      if (ghostSim && ghostPlayer && ghostRecord) {
        while (
          ghostSim.tick < targetTick &&
          ghostSim.status === "running" &&
          ghostSim.tick < ghostRecord.endTick
        ) {
          ghostSim.step(ghostPlayer.frameAt(ghostSim.tick + 1));
        }
        if (ghostSim.status !== "running" || ghostSim.tick >= ghostRecord.endTick) {
          ghostMesh.setEnabled(false);
        }
      }
      if (sim.status === "dead") onDeath();
      else if (sim.status === "finished") onFinish();

      const startReal = sim.startTick * SIM_DT;
      if (conductor.realTime < startReal) {
        hud.setCountIn(Math.ceil((startReal - conductor.realTime) / ctx.spb));
      } else {
        hud.setCountIn(null);
      }
      hud.setThrottle(sim.throttle, sim.effRate);
      audio.setIceSliding(sim.onIce && sim.status === "running");

      if (ctx.level.prompts.length > 0) {
        const row = Math.floor(sim.z / 2);
        const active = ctx.level.prompts.find((p) => row >= p.row && row < p.row + 16);
        hud.setTutorialPrompt(active?.text ?? null);
      }
    }

    // render interpolated poses one REAL tick in the past (D-002, R2 §1.2)
    if (sim && conductor && ctx) {
      const realTime = Math.max(sim.startTick * SIM_DT, conductor.realTime);
      const displayReal = Math.min(
        Math.max(sim.startTick * SIM_DT, realTime - SIM_DT),
        sim.tick * SIM_DT,
      );
      const alpha = Math.min(1, Math.max(0, (displayReal - (sim.tick - 1) * SIM_DT) / SIM_DT));
      const rx = sim.prevX + (sim.x - sim.prevX) * alpha;
      const ry = sim.prevY + (sim.y - sim.prevY) * alpha;
      const rz = sim.speed * sim.songTimeAt(displayReal);

      ship.position.set(rx, ry + 0.3, rz);
      ship.rotation.z = -sim.vx * 0.012;

      if (ghostSim && ghostMesh.isEnabled()) {
        const ga = Math.min(1, Math.max(0, (displayReal - (ghostSim.tick - 1) * SIM_DT) / SIM_DT));
        const gx = ghostSim.prevX + (ghostSim.x - ghostSim.prevX) * ga;
        const gy = ghostSim.prevY + (ghostSim.y - ghostSim.prevY) * ga;
        ghostMesh.position.set(gx, gy + 0.3, ghostSim.speed * ghostSim.songTimeAt(displayReal));
        ghostMesh.rotation.z = -ghostSim.vx * 0.012;
      }

      if (state === "running") {
        camRig.update(dt, rx, ry, rz);
        track?.update(rz);
        // FOV 58°→70° over effRate 0.6→1.6 (R2 VFX-1), ~200 ms smoothing
        const span = (sim.effRate - EFFRATE_MIN) / (EFFRATE_MAX - EFFRATE_MIN);
        const clamped = Math.max(0, Math.min(1, span));
        const targetFov =
          ((FOV_MIN_DEG + (FOV_MAX_DEG - FOV_MIN_DEG) * clamped) * Math.PI) / 180;
        camera.fov += (targetFov - camera.fov) * Math.min(1, dt / FOV_SMOOTH_S);
        // engine flame stretches with the throttle (VFX-1)
        flame.scaling.set(0.7 + 0.5 * clamped, 0.7 + 0.5 * clamped, 0.35 + 1.25 * clamped);
        // speed-lines fade in above 1.1 (High/Med, motion-safe)
        hud.setSpeedLines(
          speedLinesOn
            ? (sim.effRate - SPEEDLINE_MIN_EFFRATE) / (EFFRATE_MAX - SPEEDLINE_MIN_EFFRATE)
            : 0,
        );
        // near-miss whoosh (P1): a block one lane over at speed, ≤1/beat
        const row = Math.floor(rz / ROW_DEPTH);
        if (
          row !== nearMissRow &&
          sim.grounded &&
          sim.effRate >= NEARMISS_MIN_EFFRATE &&
          conductor.beat >= nearMissCooldownBeat
        ) {
          nearMissRow = row;
          const lane = Math.round(sim.x / CELL_WIDTH) + 3;
          const cells = ctx.level.cells[row];
          const adjacent =
            (cells?.[lane - 1]?.kind === "block") || (cells?.[lane + 1]?.kind === "block");
          if (adjacent) {
            audio.sfx("whoosh");
            nearMissCooldownBeat = conductor.beat + NEARMISS_COOLDOWN_BEATS;
          }
        }
      } else if (state === "results") {
        camRig.orbitUpdate(dt, rx, ry + 0.3, rz); // no-op unless finishing
      }
      track?.animate(dt, conductor.beat, rz, save.settings.reducedMotion);
      beatFx?.update(
        state === "running" || state === "dead" || state === "results" ? conductor.beat : -1,
        performance.now() / 1000,
        camera.position.z,
      );
      deathFx.update(dt);
      impactFx.update(dt);
      skybox?.update(performance.now() / 1000, camera.position.x, rz);
      audio.setEngineState(sim.effRate, state === "running");
      const phase = conductor.beat >= 0 ? conductor.beat - Math.floor(conductor.beat) : 1;
      trail.pulse(
        Math.exp(-phase * BEAT_PULSE_DECAY),
        state === "running" ? (dt * sim.effRate) / ctx.spb : 0,
      );

      hud.updateRun(sim.progressPct, sim.tick * SIM_DT * 1000, sim.bitsCollected, ctx.level.bits.length);
      hud.setDebug(
        `fps ${engine.getFps().toFixed(0)}  tick ${sim.tick}  z ${rz.toFixed(1)}  ` +
          `beat ${conductor.beat >= 0 ? conductor.beat.toFixed(2) : "-"} (${conductor instanceof AudioConductor ? "audio" : "silent"})` +
          `  thr${sim.throttle.toFixed(2)} eff${sim.effRate.toFixed(2)}\n` +
          `x ${sim.x.toFixed(2)}  y ${sim.y.toFixed(2)}  ${sim.grounded ? "grounded" : "air"}  ${sim.status}` +
          (sim.deathCause ? ` (${sim.deathCause})` : "") +
          (pureRun ? "  PURE" : "") +
          (practiceMode ? "  PRACTICE" : "") +
          (runMode !== "campaign" ? `  ${runMode.toUpperCase()}` : "") +
          (ghostSim && ghostMesh.isEnabled() ? "  GHOST" : ""),
      );
    }

    // joystick indicator: only meaningful while steering a live run
    hud.setJoystick(state === "running" ? touch.joyState() : null);

    scene.render();
  });

  setState("title");
}

main();
